import { t } from "@/i18n-js/instance";
import { Icon } from "@circle-react/components/shared/Icon";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useTipTapEditorContext } from "../TipTapEditorContext";
import { CloseButton } from "./CloseButton";

export interface Meta {
  description: string;
  favicon: string;
  site_name: string;
  title: string;
}

interface LinkItemProps {
  isRemoveLinkPreviewsEnabled: boolean;
  meta: Meta;
  url: string;
}

export const LinkItem = ({
  url,
  meta,
  isRemoveLinkPreviewsEnabled,
}: LinkItemProps) => {
  const { editor, setLinkPreviews, linkPreviews, isWorkflowType } =
    useTipTapEditorContext();

  const onRemove = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (linkPreviews && Object.keys(linkPreviews).includes(url)) {
      setLinkPreviews((prev: { [key: string]: boolean }) => ({
        ...prev,
        [url]: false,
      }));
    }
  };

  if (!(url && meta.title)) {
    return null;
  }

  const getHostname = (url: string): string => {
    try {
      return new URL(url).hostname;
    } catch {
      return url?.replace(/(^\w+:|^)\/\//, "");
    }
  };

  const isEditMode = editor.isEditable;

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className={classNames(
        "border-primary bg-primary hover:border-dark hover:bg-tertiary group relative flex min-w-[200px] max-w-full shrink-0 flex-col rounded-md border px-4 py-3 sm:w-[304px]",
        isEditMode
          ? "h-[68px] w-[274px] md:w-[328px]"
          : "max-h-[114px] w-[286px] md:w-[363px]",
      )}
    >
      <div className="text-default flex items-center pb-1.5">
        {meta.favicon ? (
          <img
            loading="eager"
            alt=""
            src={meta.favicon}
            className="mr-1.5 size-4"
          />
        ) : (
          <Icon type="12-globe" size={12} className="mr-1 !w-3" />
        )}
        <div className="flex-1 truncate text-xs font-semibold">
          {meta.site_name || getHostname(url)}
        </div>
      </div>
      {isEditMode ? (
        <div className="mt-0.5 flex w-full flex-row items-center truncate">
          <div className="text-dark text-sm font-semibold">{meta.title}</div>
          {meta.description && (
            <div className="text-light line-clamp-1 truncate text-sm">
              <span className="px-1">{t("typing_cursor")}</span>
              <span>{meta.description}</span>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="text-dark mb-0.5 truncate text-sm font-semibold">
            {meta.title}
          </div>
          {meta.description && (
            <div className="text-light line-clamp-2 flex-1 text-sm">
              {meta.description}
            </div>
          )}
        </>
      )}
      {isRemoveLinkPreviewsEnabled && !isWorkflowType && isEditMode && (
        <CloseButton onRemove={onRemove} />
      )}
    </a>
  );
};
